import { cn } from '@sohanemon/utils';
import { Portal } from '@sohanemon/utils/components';
import { useMediaQuery } from '@sohanemon/utils/hooks';
import { m } from '@sohanscript/i18n';
import { AstronautReadingLottie } from '@sohanscript/ui/animated/astronaut-reading-lottie';
import { Heading } from '@sohanscript/ui/heading';
import { PoweredBy } from '@sohanscript/ui/powered-by';
import { SohanEmon } from '@sohanscript/ui/sohanemon';
import { ClientOnly, createFileRoute } from '@tanstack/react-router';
import { Hydrate } from '@tanstack/react-start';
import { idle } from '@tanstack/react-start/hydration';
import * as React from 'react';
import { About } from '#/components/about.tsx';
import { Contact } from '#/components/contact.tsx';
import { Cta } from '#/components/cta.tsx';
import { ExperienceSection } from '#/components/experiences/exp-section.tsx';
import { GoToTop } from '#/components/layout/goto-top.tsx';
import { MainHero } from '#/components/main-hero.tsx';
import { NotesPreview } from '#/components/notes/notes-preview.tsx';
import { Stack } from '#/components/stack.tsx';
import { Testimonials } from '#/components/testimonials.tsx';
import { ProjectSlider } from '#/components/works/project-slider.tsx';
import { DetatchedWindow } from '../-components/detatched-window';

export const Route = createFileRoute('/_main/')({
	component: App,
	headers: () => ({
		'Cache-Control': 'public, max-age=3600, s-maxage=86400',
	}),
});

const SECTION_CLASS =
	'select-none md:min-h-[max(800px,calc(100svh-56px))] grid place-items-center';

function App() {
	const isLg = useMediaQuery('lg');

	return (
		<>
			<ClientOnly
				fallback={
					<div className="grid h-svh place-content-center">
						<SohanEmon />
					</div>
				}
			>
				{isLg ? <DetatchedWindow /> : <MainHero className="p-4" />}
			</ClientOnly>
			<React.Suspense fallback={<div className="h-[400vh]" />}>
				<section className="max-md:space-y-10">
					{/* ── 1. About (mobile only; desktop uses DetatchedWindow) ── */}
					<Hydrate when={idle()}>
						<About className={cn('lg:sr-only', SECTION_CLASS)} />
					</Hydrate>

					{/* ── 2. Works: proof comes first ── */}
					<div
						id="works"
						className={cn('w-full overflow-hidden py-20', SECTION_CLASS)}
					>
						<Hydrate when={idle()}>
							<Heading
								titlePrefix={m.nav_works()}
								titleHighlight={m.heading_works_highlight()}
								description={m.works_header_tagline()}
								ampersand
								className="mb-12"
							/>
							<ProjectSlider />
						</Hydrate>
					</div>

					{/* ── 3. Experience: where those things were built ── */}
					<Hydrate when={idle()}>
						<ExperienceSection className="lg:pt-32" id="experiences" />
					</Hydrate>

					{/* ── 4. Stack: what tools ── */}
					<Hydrate when={idle()}>
						<Stack className={cn(SECTION_CLASS, 'min-h-175!')} id="skills" />
					</Hydrate>

					{/* ── 5. Notes: deeper dives ── */}
					<div id="notes" className={cn('w-full py-20', SECTION_CLASS)}>
						<Heading
							titlePrefix={m.nav_notes()}
							titleHighlight={m.heading_notes_highlight()}
							description={m.notes_sub()}
							ampersand
							className="container mb-12"
						/>
						<Hydrate when={idle()}>
							<NotesPreview />
						</Hydrate>
					</div>

					{/* ── 6. Testimonials: social proof before action ── */}
					<Hydrate when={idle()}>
						<Testimonials />
					</Hydrate>

					{/* ── 7. CTA: ask after proof + trust ── */}
					<Hydrate when={idle()}>
						<Cta className="container flex min-h-[70vh] flex-col justify-center" />
					</Hydrate>

					{/* ── 8. PoweredBy: trust decoration ── */}
					<div
						className={cn(
							'top-0 origin-top pt-16 lg:sticky lg:h-screen lg:pt-32',
							SECTION_CLASS,
							'content-start justify-items-center',
						)}
						id="testimonials"
					>
						<Hydrate when={idle()}>
							<PoweredBy id="powered-by" />
						</Hydrate>
					</div>

					{/* ── 9. Contact ── */}
					<div
						className={cn(
							'relative lg:border-t',
							'min-h-[max(800px,calc(100vh-120px))]!',
							SECTION_CLASS,
						)}
						id="contact"
					>
						<Hydrate when={idle()}>
							<Portal container="#home--contact-form">
								<AstronautReadingLottie className="z-50 size-88 -right-36 max-sm:hidden -top-60 absolute" />
							</Portal>
							<Contact
								id="home--contact-form"
								transparent={false}
								className="grid place-items-center "
							/>
						</Hydrate>
					</div>
				</section>
			</React.Suspense>
			<GoToTop
				hideHomeLink
				hideContactLink
				links={[
					{ href: '#works', icon: 'lucide:container', label: m.nav_works() },
					{
						href: '#experiences',
						icon: 'lucide:briefcase-business',
						label: m.gt_experiences(),
					},
					{ href: '#skills', icon: 'ph:stack-duotone', label: m.gt_skills() },
					{
						href: '#notes',
						icon: 'solar:document-add-linear',
						label: m.nav_notes(),
					},
					{
						href: '#cta',
						icon: 'lucide:rocket',
						label: m.gt_get_started(),
					},
					{
						href: '#powered-by',
						icon: 'lucide:zap',
						label: m.gt_powered_by(),
					},
					{ href: '#contact', icon: 'mage:message', label: m.nav_contact() },
				]}
			/>
		</>
	);
}
